import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectMetadataFor } from '../../store/selectors/index.js';
import { metadataClosed } from '../../store/slices/uiSlice.js';
import { useGetCatalogRecordQuery } from '../../services/catalogApi.js';

/**
 * Dataset details.
 *
 * Renders the catalog record as readable prose rather than dumping raw FGDC or
 * ISO XML at the user, which is what the existing viewer does. The keywords are
 * shown because they are genuinely useful for finding related data, not because
 * the metadata standard happens to include them.
 */
export default function MetadataDialog() {
  const dispatch = useAppDispatch();
  const id = useAppSelector(selectMetadataFor);
  const { data, isLoading } = useGetCatalogRecordQuery(id ?? '', { skip: !id });

  const close = () => dispatch(metadataClosed());

  return (
    <Dialog open={Boolean(id)} onClose={close} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, fontSize: '1rem', fontWeight: 650 }}>
        {isLoading ? 'Loading…' : (data?.title ?? 'Dataset')}
        <IconButton
          onClick={close}
          aria-label="Close details"
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {data && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip size="small" label={data.category} color="primary" variant="outlined" />
              <Chip
                size="small"
                label={data.renderable ? 'Available as a map layer' : 'Metadata only'}
                variant="outlined"
                color={data.renderable ? 'secondary' : 'default'}
              />
            </Box>

            {data.abstract && (
              <Box>
                <Typography variant="h3" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                  Description
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.abstract}
                </Typography>
              </Box>
            )}

            {data.layerName && (
              <Box>
                <Typography variant="h3" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                  Service layer
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                  {data.layerName}
                </Typography>
              </Box>
            )}

            {data.subjects.length > 0 && (
              <Box>
                <Typography variant="h3" sx={{ fontSize: '0.8125rem', mb: 0.75 }}>
                  Keywords
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {data.subjects.map((s) => (
                    <Chip key={s} label={s} size="small" sx={{ height: 20, fontSize: '0.6875rem' }} />
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              Published by the Oklahoma Office of Geographic Information. Catalog identifier{' '}
              <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                {data.id}
              </Box>
              .
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
